/**
 * "Exportera till CSV" — bulk export of contracts (per csv-plan.md /
 * ADR-0067). Output is column-stable per template *version*, so the modal
 * lists the template types present in the currently-loaded (and filtered)
 * list grouped by `templateVersionId` and lets the user pick **one or more**
 * — each chosen version becomes its own file-set (a folder in the bundle).
 *
 * Options: a CSV bundle (contracts / parties / properties / codebook / per-
 * table companions) or an Excel workbook; code-headed (canonical) or human
 * labels. The selection + the active search are forwarded to the binary
 * endpoint, which re-applies the same visibility the list uses, so the
 * bundle never widens beyond what the user can already see. The download
 * uses the server-supplied filename.
 */

import { useMemo, useState } from 'react';
import { tenantApi, ApiError, type ContractListRow } from '../api.js';

type TemplateOption = {
  templateVersionId: string;
  templateName: string;
  templateVersion: string;
  templateKey: string;
  count: number;
};

function buildOptions(contracts: ReadonlyArray<ContractListRow>): TemplateOption[] {
  const byVersion = new Map<string, TemplateOption>();
  for (const c of contracts) {
    const existing = byVersion.get(c.templateVersionId);
    if (existing) {
      existing.count += 1;
    } else {
      byVersion.set(c.templateVersionId, {
        templateVersionId: c.templateVersionId,
        templateName: c.templateName,
        templateVersion: c.templateVersion,
        templateKey: c.templateKey,
        count: 1,
      });
    }
  }
  return [...byVersion.values()].sort((a, b) =>
    a.templateName.localeCompare(b.templateName, 'sv') ||
    a.templateVersion.localeCompare(b.templateVersion, 'sv'),
  );
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function ExportCsvModal({
  contracts,
  query,
  onClose,
}: {
  contracts: ReadonlyArray<ContractListRow>;
  query: string;
  onClose: () => void;
}) {
  const options = useMemo(() => buildOptions(contracts), [contracts]);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(options.length === 1 ? [options[0]!.templateVersionId] : []),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [readable, setReadable] = useState(false);
  const [format, setFormat] = useState<'csv' | 'xlsx'>('csv');

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const onExport = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBusy(true);
    setErr(null);
    setWarning(null);
    try {
      const { blob, count, truncated, filename } = await tenantApi.contracts.export_csv({
        templateVersionIds: ids,
        q: query || undefined,
        headers: readable ? 'labels' : undefined,
        format,
      });
      triggerDownload(blob, filename);
      if (truncated) {
        // The bundle is partial — keep the modal open so the warning is seen.
        setWarning(
          `Exporten begränsades till ${count} avtal. Förfina sökningen för att exportera färre åt gången.`,
        );
      } else {
        onClose();
      }
    } catch (e) {
      setErr(
        e instanceof ApiError ? e.message : 'Exporten misslyckades. Försök igen.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-6"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-lg flex-col gap-3 rounded bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Exportera avtal</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-stone-500 hover:text-stone-800"
          >
            Stäng
          </button>
        </div>

        <p className="text-xs leading-relaxed text-stone-500">
          Export sker per avtalstyp. Välj en eller flera typer att exportera
          {query ? ' (filtrerat på din sökning)' : ''}. Du får avtal, parter,
          fastigheter och en kodbok – som CSV-filer (ZIP) eller en Excel-fil.
        </p>

        {err && (
          <div className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700">
            {err}
          </div>
        )}

        {warning && (
          <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
            {warning}
          </div>
        )}

        {options.length === 0 ? (
          <div className="rounded border border-dashed border-stone-300 p-4 text-center text-xs text-stone-500">
            Inga avtal att exportera.
          </div>
        ) : (
          <ul className="max-h-[50vh] space-y-1 overflow-y-auto">
            {options.map((o) => (
              <li key={o.templateVersionId}>
                <label className="flex cursor-pointer items-center gap-2 rounded border border-stone-200 p-2 text-sm hover:bg-stone-50">
                  <input
                    type="checkbox"
                    checked={selected.has(o.templateVersionId)}
                    onChange={() => toggle(o.templateVersionId)}
                  />
                  <span className="min-w-0 flex-1 truncate">
                    {o.templateName}{' '}
                    <span className="text-stone-400">{o.templateVersion}</span>
                  </span>
                  <span className="shrink-0 text-xs text-stone-500">
                    {o.count} st
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}

        {options.length > 0 && (
          <div className="flex flex-col gap-2 pt-1">
            <div className="flex items-center gap-3 text-xs text-stone-600">
              <span className="text-stone-500">Format:</span>
              {(['csv', 'xlsx'] as const).map((f) => (
                <label key={f} className="flex cursor-pointer items-center gap-1.5">
                  <input
                    type="radio"
                    name="export-format"
                    checked={format === f}
                    onChange={() => setFormat(f)}
                  />
                  {f === 'csv' ? 'CSV (ZIP)' : 'Excel (.xlsx)'}
                </label>
              ))}
            </div>
            <label className="flex cursor-pointer items-start gap-2 text-xs text-stone-600">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={readable}
                onChange={(e) => setReadable(e.target.checked)}
              />
              <span>
                Läsbara rubriker och värden (etiketter och Ja/Nej istället för
                koder). Annars exporteras stabila koder enligt kodboken.
              </span>
            </label>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50"
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={onExport}
            disabled={busy || selected.size === 0}
            className="rounded bg-stone-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-stone-800 disabled:opacity-40"
          >
            {busy ? 'Exporterar…' : 'Exportera'}
          </button>
        </div>
      </div>
    </div>
  );
}
